export function getQueryParam(key: string): string | undefined {
    if (typeof window === 'undefined') {
        return undefined;
    }
    return new URLSearchParams(window.location.search).get(key) ?? undefined;
}

/**
 * Reads the given query parameters from `window.location.search` and removes
 * them from the URL bar via `history.replaceState` (no navigation). Returns a
 * record keyed by each requested name with the raw string value, or `null` if
 * the param wasn't present. Removing the params prevents re-application if a
 * consumer's effect re-runs. Pathname and hash are preserved.
 */
export function consumeParamsFromUrl<T extends string>(
    paramNames: readonly T[],
): Record<T, string | null> {
    const result = Object.fromEntries(paramNames.map((name) => [name, null])) as Record<
        T,
        string | null
    >;

    if (typeof window === 'undefined') {
        return result;
    }

    const params = new URLSearchParams(window.location.search);
    let changed = false;
    for (const name of paramNames) {
        const value = params.get(name);
        if (value !== null) {
            result[name] = value;
            params.delete(name);
            changed = true;
        }
    }

    if (changed) {
        const queryString = params.toString();
        const newUrl = `${window.location.pathname}${queryString ? `?${queryString}` : ''}${window.location.hash}`;
        window.history.replaceState(null, '', newUrl);
    }

    return result;
}
